import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, RefreshCw } from "lucide-react";

/**
 * ScanStatsBox — Compteur discret des scans QR (bâches + t-shirts).
 * Aucun tracker externe (Umami / Plausible) : les scans sont enregistrés
 * dans MongoDB côté backend (endpoint /api/track/scan) et agrégés ici.
 */
export default function ScanStatsBox({ API }) {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/track/scans-stats`, {
        params: { pin: "SAFUAYE2024", days },
      });
      setStats(r.data);
    } catch (e) {
      console.error("scan stats:", e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  const labels = {
    "bache-siege": "🎪 Bâche Siège",
    "bache-academie": "⚽ Bâche Académie",
    "bache-ifodje": "🏆 Bâche Ifodjè FC",
    "tshirt-academie": "👕 T-shirt Académie",
    "tshirt-gala": "👕 T-shirt Gala",
    "tshirt-bureau": "👕 T-shirt Bureau",
    "carte-atakpame": "💳 Carte visite · Atakpamé",
    "carte-lome": "💳 Carte visite · Lomé",
    "carte-diaspora": "💳 Carte visite · Diaspora (UK · FR · IT)",
    "carte-partenaires": "💳 Carte visite · Partenaires",
    "carte-uk": "🇬🇧 Carte visite UK (bilingue premium)",
    "carte-uk-apedo": "🎩 Carte perso · APEDO Chair (UK)",
  };

  return (
    <div className="bg-slate-900 rounded-lg p-4 border-2 border-slate-700 mb-3 text-white" data-testid="scan-stats-box">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <p className="font-heading font-bold text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-400" />
            📊 Statistiques scans QR (bâches + t-shirts)
          </p>
          <p className="text-xs text-slate-400">
            Chaque scan de QR est comptabilisé anonymement (IP tronquée · aucune donnée personnelle).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  data-testid="scan-stats-days"
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs">
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
            <option value={365}>1 an</option>
          </select>
          <button onClick={load} disabled={loading}
                  data-testid="scan-stats-refresh"
                  className="p-2 bg-slate-800 hover:bg-slate-700 rounded">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      {!stats ? (
        <p className="text-xs text-slate-400 italic">Chargement…</p>
      ) : (
        <>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-4xl font-bold text-emerald-400" data-testid="scan-stats-total">
              {stats.total}
            </span>
            <span className="text-sm text-slate-300">scans sur {stats.days} jours</span>
          </div>
          {stats.by_source.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              Aucun scan enregistré pour le moment. Imprimez les bâches et surveillez cet écran !
            </p>
          ) : (
            <div className="space-y-1">
              {stats.by_source.map((s) => {
                const max = stats.by_source[0].count || 1;
                const pct = (s.count / max) * 100;
                return (
                  <div key={s.src} className="flex items-center gap-2 text-xs" data-testid={`scan-src-${s.src}`}>
                    <span className="w-40 truncate">{labels[s.src] || s.src}</span>
                    <div className="flex-1 bg-slate-800 rounded h-4 relative">
                      <div className="bg-emerald-500 h-4 rounded"
                           style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-10 text-right font-mono">{s.count}</span>
                  </div>
                );
              })}
            </div>
          )}
          {stats.by_day.length > 0 && (
            <div className="mt-3 border-t border-slate-700 pt-2">
              <p className="text-[10px] uppercase text-slate-400 mb-1">Timeline (jour par jour)</p>
              <div className="flex items-end gap-1 h-16">
                {stats.by_day.slice(-30).map((d, i) => {
                  const max = Math.max(...stats.by_day.map(x => x.count));
                  const h = max ? (d.count / max) * 100 : 0;
                  return (
                    <div key={i} className="flex-1 bg-emerald-500/60 hover:bg-emerald-400 rounded-t transition-colors"
                         style={{ height: `${h}%`, minHeight: '2px' }}
                         title={`${d.day} : ${d.count}`} />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
